import { createRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toast } from "@/lib/safe-toast";
import { Settings, BarChart3 } from "lucide-react";
import { authenticatedRoute } from "../_authenticated";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Select,
} from "@/components/ui";
import { profileInvalidateKeys, useProfile } from "@/hooks/useAdmin";
import { useAuth } from "@/contexts/useAuth";
import { apiPatch } from "@/api";
import { useQueryClient } from "@tanstack/react-query";
import { toDisplayString, toSafeReactNode } from "@/lib/utils";
import { getStoredAnalyticsConsent, clearStoredAnalyticsConsent } from "@/lib/analytics-consent";
import { isPostHogAvailable, applyPostHogConsent } from "@/lib/posthog";
import { applyLocale } from "@/i18n";
import { SUPPORTED_LOCALES } from "@takelead/shared";

/**
 * Derived from the single source of truth — a hand-maintained list would drift
 * from SUPPORTED_LOCALES (it did: the switcher missed the 9 locales of I18N-04).
 * Each code needs a `settings.locale_<code>` native-name key and a locale
 * directory; the SSOT ↔ directories ↔ native-name-keys set equality is locked
 * by apps/web/test/i18n-key-parity.test.ts (the parity gate alone only checks
 * en → locale, so it would miss a code added here without files behind it).
 */
const LOCALE_OPTIONS = SUPPORTED_LOCALES;

const CURRENCY_OPTIONS = [
  "EUR", "USD", "GBP", "CHF", "PLN", "CZK", "SEK", "NOK", "DKK",
  "JPY", "CNY", "KRW", "INR", "BRL", "TRY", "RUB", "AUD", "CAD",
] as const;

const TIMEZONE_OPTIONS = [
  "Europe/Berlin",
  "Europe/London",
  "Europe/Paris",
  "Europe/Zurich",
  "Europe/Vienna",
  "Europe/Amsterdam",
  "Europe/Warsaw",
  "Europe/Prague",
  "Europe/Stockholm",
  "Europe/Istanbul",
  "Europe/Moscow",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "America/Toronto",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Seoul",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Bangkok",
  "Asia/Ho_Chi_Minh",
  "Australia/Sydney",
  "Pacific/Auckland",
] as const;

function SettingsPage() {
  const { t } = useTranslation("common");
  const { data: profile, isLoading } = useProfile();
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const queryClient = useQueryClient();

  const [locale, setLocale] = useState("");
  const [currency, setCurrency] = useState("");
  const [timezone, setTimezone] = useState("");
  const [saving, setSaving] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState<"accepted" | "rejected" | null>(() =>
    getStoredAnalyticsConsent()
  );

  useEffect(() => {
    if (!profile) return;
    setLocale(profile.preferred_locale ?? "");
    setCurrency(profile.preferred_currency ?? "");
    setTimezone(profile.timezone ?? "");
    if (profile.analytics_consent_accepted === true) setAnalyticsConsent("accepted");
    else if (profile.analytics_consent_accepted === false) setAnalyticsConsent("rejected");
  }, [profile]);

  const hasChanges =
    locale !== (profile?.preferred_locale ?? "") ||
    currency !== (profile?.preferred_currency ?? "") ||
    timezone !== (profile?.timezone ?? "");

  const save = async () => {
    if (!token || !hasChanges) return;
    setSaving(true);
    try {
      const body: Record<string, string | null> = {};
      if (locale !== (profile?.preferred_locale ?? "")) {
        body.preferred_locale = locale || null;
      }
      if (currency !== (profile?.preferred_currency ?? "")) {
        body.preferred_currency = currency || null;
      }
      if (timezone !== (profile?.timezone ?? "")) {
        body.timezone = timezone || null;
      }
      const res = await apiPatch("/me", token, body);
      if (res.ok) {
        if (body.preferred_locale !== undefined) {
          await applyLocale(locale || null);
        }
        toast.success(t("settings.saveSuccess"));
        for (const queryKey of profileInvalidateKeys(token)) {
          void queryClient.invalidateQueries({ queryKey });
        }
      } else {
        toast.error(t("settings.saveError"));
      }
    } catch {
      toast.error(t("settings.saveError"));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("settings.pageTitle"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("common.loading"))}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("settings.pageTitle"))}</h1>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" aria-hidden />
            {toSafeReactNode(t("settings.languageRegion"))}
          </CardTitle>
          <CardDescription>
            {toSafeReactNode(t("settings.languageRegionDescription"))}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <label htmlFor="settings-locale" className="text-sm font-medium text-text">
                {toSafeReactNode(t("settings.language"))}
              </label>
              <Select
                id="settings-locale"
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
              >
                <option value="">{toSafeReactNode(t("settings.auto"))}</option>
                {LOCALE_OPTIONS.map((code) => (
                  <option key={toDisplayString(code)} value={toDisplayString(code)}>
                    {toSafeReactNode(t(`settings.locale_${code}` as "settings.locale_de") || code)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="settings-currency" className="text-sm font-medium text-text">
                {toSafeReactNode(t("settings.currency"))}
              </label>
              <Select
                id="settings-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option value="">{toSafeReactNode(t("settings.auto"))}</option>
                {CURRENCY_OPTIONS.map((code) => (
                  <option key={toDisplayString(code)} value={toDisplayString(code)}>
                    {toSafeReactNode(t(`settings.currency_${code}` as "settings.currency_EUR") || code)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="settings-timezone" className="text-sm font-medium text-text">
                {toSafeReactNode(t("settings.timezone"))}
              </label>
              <Select
                id="settings-timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                <option value="">{toSafeReactNode(t("settings.auto"))}</option>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={toDisplayString(tz)} value={toDisplayString(tz)}>
                    {toDisplayString(tz).replace(/_/g, " ")}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              variant="primary"
              disabled={!hasChanges || saving}
              onClick={save}
            >
              {saving ? toSafeReactNode(t("settings.saving")) : toSafeReactNode(t("settings.save"))}
            </Button>
            {hasChanges && (
              <p className="text-xs text-text-muted">{toSafeReactNode(t("settings.unsavedChanges"))}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {isPostHogAvailable() && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" aria-hidden />
              {toSafeReactNode(t("settings.analyticsTitle"))}
            </CardTitle>
            <CardDescription>
              {toSafeReactNode(t("settings.analyticsDescription"))}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {analyticsConsent === "accepted" ? (
              <p className="text-sm text-text-muted mb-3">
                {toSafeReactNode(t("settings.analyticsAccepted"))}
              </p>
            ) : analyticsConsent === "rejected" ? (
              <p className="text-sm text-text-muted mb-3">
                {toSafeReactNode(t("settings.analyticsRejected"))}
              </p>
            ) : null}
            {analyticsConsent !== null && (
              <Button
                variant="outline"
                onClick={async () => {
                  clearStoredAnalyticsConsent();
                  setAnalyticsConsent(null);
                  if (token) {
                    await apiPatch("/me", token, { analytics_consent_accepted: false });
                    for (const queryKey of profileInvalidateKeys(token)) {
                      void queryClient.invalidateQueries({ queryKey });
                    }
                  }
                  applyPostHogConsent("rejected");
                  toast.success(t("settings.consentResetToast"));
                }}
              >
                {analyticsConsent === "accepted" ? toSafeReactNode(t("settings.revokeConsent")) : toSafeReactNode(t("settings.resetChoice"))}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "settings",
  component: SettingsPage,
});
